/**
 * CancelAppointmentDialog.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Confirmação do botão "Cancelar" do painel do agendamento (feedback #17).
 *
 * A pessoa escolhe quem cancelou (pela clínica = status `canceled`; pela
 * pessoa atendida = `canceled_by_patient`, o padrão, que era o comportamento
 * antigo do botão) e se quer avisar pelo WhatsApp. O aviso só é oferecido
 * quando há telefone e o horário ainda não passou; o texto de apoio diz se ele
 * sai pelo sistema ou abre o WhatsApp Web com a mensagem pronta.
 *
 * Mesmo toggle de ReservationPanel ("Liberar horário").
 *
 * @module CancelAppointmentDialog
 */

import React, { useState, useEffect } from 'react';
import ConfirmDialog from '../../../../../components/ConfirmDialog/ConfirmDialog';
import { Canceled, CanceledByPatient } from '../../../../../consts/AppointmentsState';
import styles from './CancelAppointmentDialog.module.css';

const WHO_OPTIONS = [
  { id: CanceledByPatient, label: 'Cancelado pelo paciente', hint: 'Quem seria atendido desistiu ou pediu para cancelar.' },
  { id: Canceled,          label: 'Cancelado',               hint: 'Cancelado por quem atende.' },
];

/**
 * @param {Object}   props
 * @param {boolean}  props.isOpen
 * @param {string}   [props.patientName]
 * @param {string}   [props.dateLabel]      - "dd/mm/aaaa às hh:mm"
 * @param {string}   [props.initialStatus] - Rádio pré-marcado (padrão: pela pessoa atendida)
 * @param {boolean}  props.canNotify        - Há telefone e o horário ainda não passou
 * @param {boolean}  props.notifyViaSystem  - O aviso sai pelo sistema (senão abre o WhatsApp Web)
 * @param {boolean}  props.loading
 * @param {Function} props.onConfirm        - ({ status, notify }) => void
 * @param {Function} props.onCancel
 */
export default function CancelAppointmentDialog({
  isOpen,
  patientName,
  dateLabel,
  initialStatus = CanceledByPatient,
  canNotify,
  notifyViaSystem,
  loading,
  onConfirm,
  onCancel,
}) {
  const [status, setStatus] = useState(CanceledByPatient);
  const [notify, setNotify] = useState(true);

  // Cada abertura começa do status pré-marcado (pela pessoa atendida, salvo quem
  // abriu pelo dropdown escolhendo "Cancelado"), com aviso ligado
  useEffect(() => {
    if (!isOpen) return;
    setStatus(initialStatus || CanceledByPatient);
    setNotify(true);
  }, [isOpen, initialStatus]);

  const message = (
    <span>
      O horário volta a ficar livre na agenda.
      {(patientName || dateLabel) && (
        <span className={styles.summary}>
          {patientName && <span className={styles.summaryLine}>Paciente: <strong>{patientName}</strong></span>}
          {dateLabel   && <span className={styles.summaryLine}>Data: <strong>{dateLabel}</strong></span>}
        </span>
      )}
    </span>
  );

  return (
    <ConfirmDialog
      isOpen={isOpen}
      title="Cancelar agendamento"
      message={message}
      confirmLabel="Cancelar agendamento"
      cancelLabel="Voltar"
      variant="danger"
      onConfirm={() => onConfirm?.({ status, notify: canNotify && notify })}
      onCancel={onCancel}
      loading={loading}
    >
      <div className={styles.whoGroup} role="radiogroup" aria-label="Quem cancelou">
        {WHO_OPTIONS.map(opt => (
          <label key={opt.id} className={`${styles.whoOption} ${status === opt.id ? styles.whoOptionActive : ''}`}>
            <input
              type="radio"
              name="cancel-appointment-who"
              className={styles.whoInput}
              value={opt.id}
              checked={status === opt.id}
              onChange={() => setStatus(opt.id)}
              disabled={loading}
            />
            <span className={styles.whoText}>
              <span className={styles.whoLabel}>{opt.label}</span>
              <span className={styles.whoHint}>{opt.hint}</span>
            </span>
          </label>
        ))}
      </div>

      {canNotify && (
        <div className={styles.notifyRow}>
          <span className={styles.notifyLabel}>
            Avisar pelo WhatsApp
            <span className={styles.notifyHint}>
              {notifyViaSystem ? 'Sai pelo sistema, na hora.' : 'Abre o WhatsApp com a mensagem pronta.'}
            </span>
          </span>
          <label className={styles.toggleWrapper}>
            <input
              type="checkbox"
              className={styles.toggleInput}
              checked={notify}
              onChange={(e) => setNotify(e.target.checked)}
              disabled={loading}
              aria-label="Avisar pelo WhatsApp"
            />
            <span className={styles.toggleSlider} />
          </label>
        </div>
      )}
    </ConfirmDialog>
  );
}
